import {
  Autocomplete,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { getBusinessRelationsForUser } from 'lib/api';
import { CreateForm } from 'lib/api/sgr/types';
import BusinessInput from 'lib/common/BusinessInput';
import { CURRENCIES, SGR_TYPES } from 'lib/consts';
import { isAdmin } from 'lib/functions/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

type Props = {
  form: CreateForm;
  setForm: (form: CreateForm) => void;
};

type Business = {
  identifier: string;
  businessName: string;
};

const CreateWarrantyForm: React.FC<Props> = ({ form, setForm }) => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const userIsAdmin = auth.user ? isAdmin(auth.user) : false;
  const [businesses, setBusinesses] = useState<Business[]>([]);

  const businessError = useMemo(
    () => (form.businessIdentifier ? null : 'Debe seleccionar una empresa'),
    [form.businessIdentifier],
  );

  const selectedBusiness =
    businesses.find(
      (business) => business.identifier === form.businessIdentifier,
    ) ?? null;

  const daysDifference = dayjs(form.endDate).diff(form.startDate, 'day');

  const updateField = (updates: Partial<CreateForm>) => {
    setForm({ ...form, ...updates });
  };

  const setUpBusinesses = async () => {
    try {
      const results = await getBusinessRelationsForUser(dispatch);
      if (results) setBusinesses(results.relations);
    } catch (error) {
      console.error(error);
    }
  };

  const formatBusinessLabel = (business: Business) => {
    if (!business.businessName) return business.identifier;
    return `${business.businessName} - ${business.identifier}`;
  };

  useEffect(() => {
    void setUpBusinesses();
  }, []);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Autocomplete
          options={businesses}
          getOptionLabel={(option) => formatBusinessLabel(option)}
          isOptionEqualToValue={(option, value) =>
            option?.identifier === value?.identifier
          }
          value={selectedBusiness}
          onChange={(event, newValue) => {
            updateField({ businessIdentifier: newValue?.identifier ?? null });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Cliente"
              error={!!businessError}
              required
            />
          )}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="ID Licitación"
          value={form.tenderId}
          onChange={(e) => updateField({ tenderId: e.target.value })}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          select
          fullWidth
          label="Tipo de Garantía"
          value={form.type}
          onChange={(e) => updateField({ type: e.target.value })}
          required
        >
          {SGR_TYPES.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Monto Cotizado"
          type="number"
          value={form.quotedAmount === 0 ? '' : form.quotedAmount}
          onChange={(e) =>
            updateField({ quotedAmount: Number(e.target.value) })
          }
          inputProps={{ step: 'any', min: 0 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {form.quotedCurrency}
              </InputAdornment>
            ),
          }}
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          select
          fullWidth
          label="Moneda"
          value={form.quotedCurrency}
          onChange={(e) => updateField({ quotedCurrency: e.target.value })}
          required
        >
          {CURRENCIES.map((currency) => (
            <MenuItem key={currency.value} value={currency.value}>
              {currency.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <DatePicker
            label="Fecha Inicio"
            value={form.startDate}
            onChange={(date) => updateField({ startDate: date })}
            minDate={userIsAdmin ? undefined : dayjs().subtract(7, 'day')}
            maxDate={userIsAdmin ? undefined : dayjs().add(7, 'day')}
            format="DD/MM/YYYY"
            slotProps={{
              textField: { fullWidth: true, required: true },
            }}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} md={6}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <DatePicker
            label="Fecha Término"
            value={form.endDate}
            onChange={(date) => updateField({ endDate: date })}
            format="DD/MM/YYYY"
            slotProps={{ textField: { fullWidth: true, required: true } }}
          />
        </LocalizationProvider>
        {daysDifference > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            Duración de la garantía: {daysDifference} días
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} md={6}>
        <BusinessInput
          key={form.principalRut}
          identifier={form.principalRut}
          unitCode={form.unitCode}
          onChange={(principalRut, unitCode) =>
            updateField({ principalRut, unitCode })
          }
          required
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel id="fogape-select-label">Fogape</InputLabel>
          <Select
            labelId="fogape-select-label"
            label="Fogape"
            value={form.fogape}
            onChange={(e) => updateField({ fogape: Number(e.target.value) })}
          >
            {Array.from({ length: 11 }, (_, i) => (
              <MenuItem key={i * 10} value={i * 10}>
                {i * 10}%
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={12}>
        <TextField
          fullWidth
          label="Glosa"
          value={form.description}
          onChange={(e) => updateField({ description: e.target.value })}
          multiline
          minRows={2}
          maxRows={2}
        />
      </Grid>
    </Grid>
  );
};

export default CreateWarrantyForm;
